import { useParams, Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";

const PAGES = {
  shipping: {
    title: "Shipping & Delivery",
    intro: "How your fragrance travels from our atelier in the Netherlands to your door.",
    body: [
      ["Dispatch", "Orders are hand-wrapped and dispatched within 3 working days. You receive tracking by email the moment your parcel leaves us."],
      ["Delivery estimates", "Netherlands: 1–2 working days. EU: 3–6 working days. Rest of world: 7–14 working days. [To be confirmed per carrier contract.]"],
      ["Shipping rates", "Flat and country-based rates are configured by the house and shown at checkout. [Final rates and free-shipping threshold to be confirmed.]"],
      ["Countries served", "We currently ship across the EU and select international destinations, configurable in the admin. [List to be confirmed before launch.]"],
    ],
  },
  returns: {
    title: "Returns & Refunds",
    intro: "Our policy for unopened fragrances.",
    body: [
      ["Window", "Unopened, unused fragrances may be returned within 14 days of delivery, in original packaging."],
      ["Opened products", "For hygiene reasons, opened or used fragrances cannot be returned unless faulty. [Confirm statutory exceptions.]"],
      ["Refunds", "Approved refunds are issued to the original payment method within 14 days of us receiving the return."],
      ["Faulty or damaged", "Contact us within 48 hours of delivery with photographs and we will make it right."],
    ],
  },
  privacy: {
    title: "Privacy Policy",
    intro: "How we handle your personal data. [Draft, to be reviewed before publication.]",
    body: [
      ["What we collect", "Contact and order details you provide, newsletter consent records, and basic analytics with your consent."],
      ["Why", "To fulfil orders, provide support, and, only with consent, send marketing and measure site performance."],
      ["Payments", "Card details are handled entirely by Stripe. We never see or store your full card number."],
      ["Your rights", "You may request access, correction or deletion of your data at any time via our contact page."],
      ["Controller", "[Company registration, address and VAT number to be confirmed by the owner before publication.]"],
    ],
  },
  terms: {
    title: "Terms & Conditions",
    intro: "The terms governing your use of this website and purchases. [Draft, pending legal review.]",
    body: [
      ["Orders", "A contract is formed once payment is confirmed by our payment provider and we send order confirmation."],
      ["Pricing", "Prices are shown in euros and include applicable taxes unless stated otherwise. [Confirm VAT handling.]"],
      ["Availability", "Fragrances are made in limited numbers and may sell out. We reserve the right to cancel and refund where stock is unavailable."],
      ["Governing law", "These terms are governed by the laws of the Netherlands. [To be confirmed.]"],
    ],
  },
  cookies: {
    title: "Cookie Policy & Preferences",
    intro: "How we use cookies and similar technologies.",
    body: [
      ["Essential", "Required for the cart, checkout and security. These are always on."],
      ["Analytics", "Used to understand and improve the experience, only set with your consent."],
      ["Marketing", "Meta and TikTok pixels for advertising, only set with your consent. [To be enabled in a later pass.]"],
      ["Managing preferences", "A consent banner and preference centre will let you change your choices at any time. [To be implemented.]"],
    ],
  },
};

export default function Legal() {
  const { slug } = useParams();
  const page = PAGES[slug];

  if (!page)
    return (
      <div className="pt-40 container-lux text-center" data-testid="legal-notfound">
        <p className="heading-serif text-3xl mb-6">Page not found</p>
        <Link to="/" className="label-mono text-blood">Return home</Link>
      </div>
    );

  return (
    <div className="pt-32 md:pt-40 pb-24" data-testid={`legal-${slug}`}>
      <div className="container-lux max-w-3xl">
        <Reveal>
          <p className="label-mono mb-6 text-blood">Legal & Care</p>
          <h1 className="heading-serif text-4xl md:text-6xl mb-6 leading-[0.95]">{page.title}</h1>
          <p className="text-base md:text-lg text-ivory/75 font-sans leading-relaxed mb-5">{page.intro}</p>
          <div className="inline-block border border-blood/40 text-blood label-mono px-4 py-2 mb-12">
            Draft — requires owner confirmation before publication
          </div>
        </Reveal>
        <div className="divide-y divide-smoke/15 border-y border-smoke/15">
          {page.body.map(([h, t]) => (
            <Reveal key={h}>
              <div className="py-8 grid md:grid-cols-3 gap-4">
                <h2 className="heading-serif text-xl md:text-2xl md:col-span-1">{h}</h2>
                <p className="md:col-span-2 text-base text-ivory/75 font-sans leading-relaxed">{t}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-12">
          <Link to="/contact" className="label-mono text-blood hover:text-ivory transition-colors">
            Questions? Contact the house →
          </Link>
        </div>
      </div>
    </div>
  );
}
